/**
 * Phase J-UX — Export & Reports collapsible panel.
 *
 * Collapsed by default. Houses:
 *   - Export custom date range as PDF
 *   - Download weekly summary
 *   - Future Court Bundle / AI summary slots (rendered as
 *     "coming soon" placeholders so the user knows where these
 *     will live)
 *
 * The page header no longer carries any export controls — the
 * primary surface is reading and writing entries.
 */
import React, { useState } from "react";
import {
  FileDown,
  ChevronDown,
  Download,
  Sparkles,
  FileText,
  ScrollText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";

function FuturePill({ label }) {
  return (
    <span
      className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm bg-[#EEF3F4] border-[#D9E1E3] text-[#6F828A]"
      aria-label="Coming soon"
    >
      {label}
    </span>
  );
}

export function JournalExportPanel({ selectedChildId, activeFilter, eventId }) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });
  const todayIso = new Date().toISOString().slice(0, 10);
  const [weeklyStart, setWeeklyStart] = useState(() => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    const day = d.getUTCDay();
    const offset = day === 0 ? 6 : day - 1;
    d.setUTCDate(d.getUTCDate() - offset);
    return d.toISOString().slice(0, 10);
  });

  const exportRange = () => {
    const qs = new URLSearchParams();
    if (range.start) qs.set("start", range.start);
    if (range.end) qs.set("end", range.end);
    if (activeFilter && activeFilter !== "all") qs.set("filter", activeFilter);
    if (eventId) qs.set("event_id", eventId);
    const url = `${API}/journal/export/pdf${qs.toString() ? `?${qs.toString()}` : ""}`;
    const fname = `journal-export-${
      activeFilter !== "all" ? `${activeFilter}-` : ""
    }${todayIso}.pdf`;
    // Iter 125.hybrid-exports — hybrid email fallback (see Expenses.jsx).
    downloadFile(url, fname, {
      label: "Generating journal PDF",
      successMessage: "PDF ready",
      asyncFallback: {
        endpoint: "/exports/journal/pdf/start",
        payload: {
          start: range.start || null,
          end: range.end || null,
          filter: (activeFilter && activeFilter !== "all") ? activeFilter : "all",
          event_id: eventId || null,
        },
      },
    });
  };

  const exportWeekly = () => {
    const url = `${API}/journal/weekly-summary.pdf?week_start=${weeklyStart}${
      selectedChildId ? `&child_id=${encodeURIComponent(selectedChildId)}` : ""
    }`;
    downloadFile(
      url,
      `coparentconsole_journal_weekly_${weeklyStart}.pdf`,
      { label: "Building weekly summary", successMessage: "Weekly summary ready" },
    );
  };

  return (
    <div
      className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden"
      data-testid="journal-export-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-3 sm:p-4 hover:bg-[#F7FAFA] transition-colors"
        aria-expanded={open}
        data-testid="journal-export-panel-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileDown className="w-4 h-4 text-[#1F9FA4] flex-shrink-0" strokeWidth={1.6} />
          <div className="text-left min-w-0">
            <div className="text-sm font-medium text-[#2E3A3F]">Export &amp; Reports</div>
            <div className="text-[11px] text-[#6F828A] hidden sm:block">
              PDF exports, weekly summaries and future AI reports
            </div>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[#6F828A] transition-transform ${
            open ? "rotate-180" : ""
          }`}
          strokeWidth={1.7}
        />
      </button>
      {open ? (
        <div
          className="border-t border-[#EEF3F4] p-3 sm:p-4 space-y-4"
          data-testid="journal-export-panel-body"
        >
          {/* Custom date range */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
              Custom date range
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3 sm:items-end w-full sm:w-auto">
                <div>
                  <Label className="text-xs text-[#6F828A]">From</Label>
                  <Input
                    type="date"
                    value={range.start}
                    onChange={(e) => setRange({ ...range, start: e.target.value })}
                    className="w-full sm:w-[170px] border-[#D9E1E3] rounded-sm"
                    data-testid="journal-range-start"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6F828A]">To</Label>
                  <Input
                    type="date"
                    value={range.end}
                    onChange={(e) => setRange({ ...range, end: e.target.value })}
                    className="w-full sm:w-[170px] border-[#D9E1E3] rounded-sm"
                    data-testid="journal-range-end"
                  />
                </div>
              </div>
              <Button
                variant="outline"
                onClick={exportRange}
                className="border-[#D9E1E3] rounded-sm w-full sm:w-auto"
                data-testid="journal-range-export-button"
              >
                <Download className="w-4 h-4 mr-2" /> Export range as PDF
              </Button>
            </div>
            <p className="text-[11px] text-[#6F828A]">
              Leave both dates empty to export every entry.
            </p>
          </div>

          {/* Weekly summary */}
          <div className="space-y-2 border-t border-[#EEF3F4] pt-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
              Weekly summary
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div>
                <Label className="text-xs text-[#6F828A]">Week starting (Monday)</Label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      const d = new Date(weeklyStart);
                      d.setUTCDate(d.getUTCDate() - 7);
                      setWeeklyStart(d.toISOString().slice(0, 10));
                    }}
                    className="flex-shrink-0 w-9 h-9 sm:w-auto sm:h-auto sm:px-2 sm:py-1 border border-[#D9E1E3] rounded-sm text-base sm:text-xs hover:bg-[#F4FAFA] inline-flex items-center justify-center"
                    data-testid="journal-weekly-prev"
                    aria-label="Previous week"
                  >
                    ‹
                  </button>
                  <Input
                    type="date"
                    value={weeklyStart}
                    onChange={(e) => setWeeklyStart(e.target.value)}
                    className="flex-1 min-w-0 sm:w-[150px] sm:flex-initial border-[#D9E1E3] rounded-sm"
                    data-testid="journal-weekly-input"
                    max={todayIso}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const d = new Date(weeklyStart);
                      d.setUTCDate(d.getUTCDate() + 7);
                      const next = d.toISOString().slice(0, 10);
                      if (next <= todayIso) setWeeklyStart(next);
                    }}
                    className="flex-shrink-0 w-9 h-9 sm:w-auto sm:h-auto sm:px-2 sm:py-1 border border-[#D9E1E3] rounded-sm text-base sm:text-xs hover:bg-[#F4FAFA] inline-flex items-center justify-center"
                    data-testid="journal-weekly-next"
                    aria-label="Next week"
                  >
                    ›
                  </button>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={exportWeekly}
                className="border-[#D9E1E3] rounded-sm w-full sm:w-auto whitespace-nowrap"
                data-testid="journal-weekly-summary-button"
              >
                <Sparkles className="w-4 h-4 mr-2 flex-shrink-0" /> Download weekly PDF
              </Button>
            </div>
          </div>

          {/* Future-proofing — AI + Court Bundle */}
          <div className="space-y-2 border-t border-[#EEF3F4] pt-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
              Coming soon
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div
                className="border border-dashed border-[#D9E1E3] rounded-sm p-3 flex items-start gap-2 text-[#6F828A]"
                data-testid="journal-future-court-bundle"
              >
                <ScrollText className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[#2E3A3F]">Send to Court Bundle</span>
                    <FuturePill label="Soon" />
                  </div>
                  <p className="text-[11px] mt-0.5">
                    Bundle selected entries with documents, expenses and
                    contact visits into a single chronological PDF.
                  </p>
                </div>
              </div>
              <div
                className="border border-dashed border-[#D9E1E3] rounded-sm p-3 flex items-start gap-2 text-[#6F828A]"
                data-testid="journal-future-ai"
              >
                <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[#2E3A3F]">AI summary &amp; themes</span>
                    <FuturePill label="Soon" />
                  </div>
                  <p className="text-[11px] mt-0.5">
                    Generate a calm, neutral monthly summary and surface
                    recurring themes across your entries.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default JournalExportPanel;
